/**
 * MONSTER TOWN — THE NEIGHBOUR ROSTER ON DISK. NPC CREW (src/npc/**).
 *
 * SAVES ARE SACRED. Section `npcs`, versioned, with hydration that cannot throw:
 * every field is validated against the list of values it is allowed to be, and a
 * row that is junk is dropped instead of taking the roster down with it. Losing
 * one neighbour is survivable; losing a kid's town is not.
 *
 * The whole look is stored per neighbour rather than an index into the archetype
 * table, so reordering or editing `roster.ts` later can never silently turn a
 * kid's favourite monster into somebody else.
 */

import { registerSaveSection } from '../core/save'
import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import type {
  MonsterBodyShape,
  MonsterEyes,
  MonsterHorns,
  MonsterPattern,
  NpcId,
} from '../core/types'
import { safePalette } from '../monster/art'
import { archetypeLook, routineForName, type Archetype } from './roster'
import {
  MAX_NPCS,
  clearNeighbours,
  movedIn,
  npcs,
  resetCounters,
  restoreNeighbour,
  townNode,
} from './registry'

interface SavedNpc {
  i: string
  n: string
  lk: string
  s: string
  c: string
  ac: string
  e: string
  h: string
  p: string
  ht: string | null
  x: number
  z: number
  k: boolean
}

export interface NpcsSave {
  lastSeenAt: number
  moved: number
  roster: SavedNpc[]
}

const SHAPES: readonly MonsterBodyShape[] = ['blob', 'tall', 'round', 'chunky']
const EYES: readonly MonsterEyes[] = ['two', 'one', 'three', 'sleepy']
const HORNS: readonly MonsterHorns[] = ['none', 'nubs', 'curved', 'antlers', 'antennae']
const PATTERNS: readonly MonsterPattern[] = ['plain', 'spots', 'stripes', 'belly', 'star']
const LIKES: readonly Archetype['likes'][] = ['wander', 'dance', 'nap', 'admire', 'visit']

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
}

/** Real-world ms of the last save. Drives "what happened while you were away". */
let lastSeenAt = 0

export function lastSeen(): number {
  return lastSeenAt
}

export function markSeenNow(): number {
  lastSeenAt = Date.now()
  return lastSeenAt
}

function serializeNpcs(): NpcsSave {
  const roster: SavedNpc[] = []
  for (const npc of npcs.values()) {
    roster.push({
      i: npc.id,
      n: npc.name,
      lk: npc.archetype.likes,
      s: npc.look.shape,
      c: npc.look.color,
      ac: npc.look.accentColor,
      e: npc.look.eyes,
      h: npc.look.horns,
      p: npc.look.pattern,
      ht: npc.look.hat,
      x: npc.walker.x,
      z: npc.walker.z,
      k: npc.known,
    })
  }
  return { lastSeenAt: Date.now(), moved: movedIn(), roster }
}

function applyNpcs(data: NpcsSave): void {
  // Clear EVERY neighbour first, in every scene — the same rule that keeps a
  // kid's empty slot from inheriting the other kid's town.
  clearNeighbours()
  lastSeenAt = data.lastSeenAt
  resetCounters(data.moved)

  const node = townNode()
  if (node === null) return

  for (const row of data.roster) {
    const archetype: Archetype = {
      name: row.n,
      shape: oneOf(row.s, SHAPES, 'blob'),
      color: oneOf(row.c, MONSTER_BODY_COLORS as readonly PaletteName[], 'monsterTeal'),
      accentColor: oneOf(row.ac, MONSTER_BODY_COLORS as readonly PaletteName[], 'monsterBelly'),
      eyes: oneOf(row.e, EYES, 'two'),
      horns: oneOf(row.h, HORNS, 'nubs'),
      pattern: oneOf(row.p, PATTERNS, 'belly'),
      hat: row.ht,
      likes: oneOf(row.lk, LIKES, 'wander'),
      // Derived from the name, never stored — see `routineForName()`. A town
      // saved before "A Day in Town" existed gets its neighbours' rhythms back
      // without a single byte of the save changing shape.
      routine: routineForName(row.n),
    }
    // `accentColor` is any palette key, not just a body colour, so it gets the
    // wider guard — junk becomes the default rather than a thrown material.
    const look = archetypeLook(archetype)
    look.accentColor = safePalette(row.ac, 'monsterBelly')
    restoreNeighbour(row.i as NpcId, archetype, look, node, row.k, row.x, row.z)
  }
}

/** Called from `installNpcs()`, i.e. BEFORE `save.load()`. */
export function registerNpcSave(): void {
  registerSaveSection<NpcsSave>(
    {
      id: 'npcs',
      version: 1,
      defaults: () => ({ lastSeenAt: 0, moved: 0, roster: [] }),
      serialize: serializeNpcs,
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const roster = c.arr<SavedNpc>(raw['roster'], (e) => {
          if (!c.isRecord(e)) return null
          const i = c.str(e['i'], '')
          if (i === '') return null
          return {
            i,
            n: c.str(e['n'], 'Neighbour'),
            lk: c.str(e['lk'], 'wander'),
            s: c.str(e['s'], 'blob'),
            c: c.str(e['c'], 'monsterTeal'),
            ac: c.str(e['ac'], 'monsterBelly'),
            e: c.str(e['e'], 'two'),
            h: c.str(e['h'], 'nubs'),
            p: c.str(e['p'], 'belly'),
            ht: typeof e['ht'] === 'string' ? e['ht'] : null,
            x: c.num(e['x'], 0),
            z: c.num(e['z'], 0),
            k: c.bool(e['k'], false),
          }
        })
        return {
          lastSeenAt: Math.max(0, c.num(raw['lastSeenAt'], 0)),
          moved: Math.max(0, c.num(raw['moved'], roster.length)),
          roster: roster.slice(0, MAX_NPCS),
        }
      },
    },
    applyNpcs,
  )
}
